import { api } from '../api/client';
import type {
  RecipeSuggestRequest,
  RecipeGeneration,
  RecipeGenerationResponse,
  RecipeGenerationsResponse,
  RecipeAdjustRequest,
  RecipeAdjustResponse,
} from '@familyhub/shared';

/**
 * Recipe generation from the pakket box (or a typed list), and the adjust chat.
 *
 * Generation is **asynchronous**: `startGeneration` kicks it off and returns a
 * `pending` batch straight away, then the screen polls `getGeneration` until it
 * is `ready`. The server stores the batch, so it survives the app closing and is
 * shared across the household's phones — and the phone never holds a request open
 * for the ~40s a set takes (which the browser reports as "offline"). Adjust stays
 * synchronous: it revises one recipe (~15s) and the answer is small.
 *
 * Both are online-only (they need the model) and write nothing to `recepten`; a
 * chosen recipe is saved through the module's own sync outbox (addRecipe). These
 * call recepten's own URLs, so they are not a cross-module seam.
 */
export async function startGeneration(body: RecipeSuggestRequest): Promise<RecipeGeneration> {
  const { generation } = await api<RecipeGenerationResponse>('/api/v1/recepten/generations', {
    method: 'POST',
    body,
  });
  return generation;
}

export async function getGeneration(id: string): Promise<RecipeGeneration> {
  const { generation } = await api<RecipeGenerationResponse>(`/api/v1/recepten/generations/${id}`);
  return generation;
}

/** The most recent batch, or null — used to resume an in-flight or finished batch on reopen. */
export async function latestGeneration(): Promise<RecipeGeneration | null> {
  const { generations } = await api<RecipeGenerationsResponse>('/api/v1/recepten/generations');
  return generations[0] ?? null;
}

export function adjustRecipe(body: RecipeAdjustRequest): Promise<RecipeAdjustResponse> {
  return api<RecipeAdjustResponse>('/api/v1/recepten/adjust', { method: 'POST', body });
}
